import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast, Slide } from 'react-toastify';

import { authApi } from '../../../core/api/auth.api';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { updateFailureToast, updateSuccessToast } from '../../../toast';
import { delay } from '../../../shared/utils';
import {
  elevRegistrationSchema,
  profesorRegistrationSchema,
  type ElevRegistrationFormData,
  type ProfesorRegistrationFormData,
} from '../validation/registration.schemas';
import { getErrorMessage, getErrorStatus } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';

/**
 * useRegisterWithForm - Registration hook using react-hook-form + Zod
 *
 * Modern implementation of registration form with:
 * - react-hook-form for state management
 * - Zod for validation
 * - shadcn Form components
 *
 * @param forRole - User role (ELEV or PROFESOR)
 * @returns form methods and handlers
 */
export function useRegisterWithForm(forRole: string) {
  const { updateToken, updateRefreshToken } = useAuthProviderV2();
  const { PATHS, PAGES, ROLES } = useRoutesProvider();
  const navigate = useNavigate();

  // Select appropriate schema based on role
  const isElev = forRole === ROLES.ELEV;

  // Initialize react-hook-form with Zod validation
  // Use separate form instances with proper typing
  const elevForm = useForm<ElevRegistrationFormData>({
    resolver: zodResolver(elevRegistrationSchema),
    defaultValues: {
      clasa: '',
      codProfesor: '',
      nume: '',
      email: '',
      parola: '',
      confirmaParola: '',
    },
    mode: 'onBlur',
  });

  const profesorForm = useForm<ProfesorRegistrationFormData>({
    resolver: zodResolver(profesorRegistrationSchema),
    defaultValues: {
      nume: '',
      telefon: '',
      email: '',
      parola: '',
      confirmaParola: '',
    },
    mode: 'onBlur',
  });

  const form = isElev ? elevForm : profesorForm;

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (data: any) => {
      await delay(1000);

      const payload: any = { ...data };
      payload.role = forRole;

      // Clean up payload
      if (!payload.telefon) payload.telefon = null;
      if (!payload.clasa) payload.clasa = null;
      if (!payload.nume) payload.nume = null;

      // Email is already transformed to lowercase by Zod schema
      delete payload.confirmaParola;

      return authApi.register(payload);
    },
    onMutate: async () => {
      const toastId = toast.loading('Se înregistrează...', { transition: Slide });
      return { toastId };
    },
    onSuccess: (data, _variables, onMutateResult) => {
      if (onMutateResult?.toastId) {
        updateSuccessToast('V-ați înregistrat cu succes!', onMutateResult.toastId);
      }
      if (data.refreshToken && data.token) {
        updateRefreshToken(data.refreshToken);
        updateToken(data.token);
      }
    },
    onError: (error, _variables, onMutateResult: any) => {
      console.error('Registration error:', error);

      const status = getErrorStatus(error);

      // Modern pattern: error.errors (transformed by response-handler)
      // Context7 Pattern: Map backend errors to form fields using react-hook-form
      const fieldErrorsApplied = isElev
        ? applyServerFieldErrors(error, elevForm.setError)
        : applyServerFieldErrors(error, profesorForm.setError);

      // Status-specific error messages
      let errorMessage = 'Am întâmpinat probleme la înregistrare.';

      if (status === 409) {
        // Conflict - email already exists. Backendul raspunde cu un mesaj generic,
        // fara `errors` structurat (masurat de V1 pe DEV) -- `applyServerFieldErrors`
        // n-are ce marca, deci campul ramane `aria-invalid="false"` desi toastul
        // spune clar care e problema (P1-4). Marcam explicit campul, dupa status.
        errorMessage = getErrorMessage(error, 'Email-ul este deja folosit. Folosește alt email.');
        if (!fieldErrorsApplied) {
          if (isElev) {
            elevForm.setError('email', { type: 'server', message: errorMessage });
          } else {
            profesorForm.setError('email', { type: 'server', message: errorMessage });
          }
        }
      } else if (status === 400) {
        // Bad request - validation errors
        errorMessage = getErrorMessage(error, 'Datele introduse nu sunt valide. Verifică câmpurile și încearcă din nou.');
      } else if ((status ?? 0) >= 500) {
        // Server errors
        errorMessage = 'Eroare de server. Încearcă din nou mai târziu.';
      } else if (!status && getErrorMessage(error, '').includes('Network')) {
        // Network errors
        errorMessage = 'Eroare de conexiune. Verifică internetul.';
      } else if (error.message) {
        // Use backend message if available
        errorMessage = error.message;
      }

      if (onMutateResult?.toastId) {
        updateFailureToast(errorMessage, onMutateResult.toastId);
      }
    },
  });

  // Form submit handler
  const handleSubmit = async (data: any) => {
    try {
      await registerMutation.mutateAsync(data);
    } catch {
      // Error handling done in mutation onError callback
    }
  };

  // Redirect to login page
  const redirectToLogIn = () => {
    navigate(PATHS[PAGES.LOGIN]);
  };

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    redirectToLogIn,
    loading: registerMutation.isPending,
    isElev,
  };
}
